import React, { useState, useEffect } from 'react';
import theme from './OptionGroupAccordion.module.scss';
import { LinkingType, OptionGroupDto } from '@/types';
import { Accordion, Dropdown, DropdownContextMenu, Badge, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { OptionGroupAccordionDetails } from './OptionGroupAccordionDetails';
import { OptionGroupDeleteDialog } from './OptionGroupDeleteDialog';
import { Plural, Trans } from '@lingui/react/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarTime,
    faCopy,
    faEdit,
    faHistory,
    faImage,
    faLink,
    faMoneyBillWave,
    faShelves,
    faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { DateDisplay } from '@/components/localization';
import { ProjectRelativeLink } from '@/components/navigation';
import { OptionGroupUnitsList } from './OptionGroupUnitsList';
import { OptionGroupRoomsList } from './OptionGroupRoomsList';
import { OptionGroupEditDialog } from '../OptionGroupEditDialog/OptionGroupEditDialog';
import OptionGroupLabel from '../OptionGroupLabel/OptionGroupLabel';
import { useQueryClient } from '@tanstack/react-query';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { getProjectCatalogProductFileThumbnailUrl } from '@/services/project-catalog';
import { ProfileIcon } from '@/components/Icons';
import HistoryLogModal from '@/seller/components/HistoryLog/HistoryLogModal';

export interface OptionGroupAccordionProps {
    optionGroup: OptionGroupDto;
    readonly?: boolean;
    isOpen?: boolean;
    accordionVariant?: React.ComponentProps<typeof Accordion>['variant'];
}

export const OptionGroupAccordion: React.FC<OptionGroupAccordionProps> = ({
    optionGroup,
    readonly = false,
    isOpen,
    accordionVariant = 'default',
}) => {
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showHistoryLogModal, setShowHistoryLogModal] = useState(false);
    // Controlled so the header can clamp the description to one line while collapsed and
    // show it in full once expanded. Kept in sync with the optional isOpen prop.
    const [open, setOpen] = useState(!!isOpen);
    useEffect(() => {
        setOpen(!!isOpen);
    }, [isOpen]);

    const queryClient = useQueryClient();
    const handleChange = () => {
        queryClient.refetchQueries({ queryKey: ['optionGroups'] });
    };

    const renderTitle = () => {
        return (
            <div className={theme.title}>
                <div className={theme.thumbnail}>
                    <div className={theme.thumbnailFrame}>
                        {optionGroup.defaultProductImage ? (
                            <Thumbnail src={getProjectCatalogProductFileThumbnailUrl(optionGroup.defaultProductImage.id)} />
                        ) : (
                            <FontAwesomeIcon icon={faImage} className={theme.thumbnailPlaceholder} />
                        )}
                    </div>
                    {optionGroup.providedByProfile && (
                        <SimpleTooltip content={optionGroup.providedByProfile.name}>
                            <div className={theme.supplierBadge}>
                                <ProfileIcon profileId={optionGroup.providedByProfile.id} sizeInPx={28} shape="circle" />
                            </div>
                        </SimpleTooltip>
                    )}
                </div>
                <div className={theme.content}>
                    <div className={theme.name}>
                        <div>
                            <OptionGroupLabel name={optionGroup.name} internalNote={optionGroup.internalNote} />
                        </div>
                    </div>

                    {optionGroup.description && (
                        <div
                            className={`${theme.description} ${
                                open ? theme.descriptionExpanded : theme.descriptionCollapsed
                            }`}>
                            {optionGroup.description}
                        </div>
                    )}

                    {optionGroup.tags.length > 0 && (
                        <div className={theme.tagsRow}>
                            <div className={theme.tagsList}>
                                {optionGroup.tags.map((tag) => (
                                    <Badge key={tag.id} size="sm" appearance="subtle">
                                        {tag.label}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {optionGroup.linkType === LinkingType.Room && optionGroup.linkedRooms.length > 0 && (
                        <div className={theme.roomsRow}>
                            <OptionGroupRoomsList optionGroup={optionGroup} />
                        </div>
                    )}

                    {optionGroup.linkType === LinkingType.Direct && (
                        <div className={theme.roomsRow}>
                            <OptionGroupUnitsList optionGroup={optionGroup} />
                        </div>
                    )}
                </div>
                <div className={theme.right}>
                    <div className={theme.meta}>
                        <span>
                            <Plural value={optionGroup.optionCount} one="# product" other="# products" />
                            <FontAwesomeIcon icon={faShelves} />
                        </span>
                        <SimpleTooltip content={<Trans>Deadline</Trans>}>
                            <span>
                                {optionGroup.deadline.name}
                                <FontAwesomeIcon icon={faCalendarTime} />
                            </span>
                        </SimpleTooltip>
                        {optionGroup.linkType === LinkingType.Direct && optionGroup.pricingField?.name && (
                            <SimpleTooltip content={<Trans>Pricing variable</Trans>}>
                                <span>
                                    {optionGroup.pricingField.name}
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                </span>
                            </SimpleTooltip>
                        )}
                        {optionGroup.linkedDate && (
                            <SimpleTooltip
                                content={
                                    optionGroup.linkedByUser ? (
                                        <Trans>Linked by {optionGroup.linkedByUser.name}</Trans>
                                    ) : (
                                        <Trans>Linked</Trans>
                                    )
                                }>
                                <span>
                                    <DateDisplay value={optionGroup.linkedDate} />
                                    <FontAwesomeIcon icon={faLink} />
                                </span>
                            </SimpleTooltip>
                        )}
                    </div>
                    {!readonly && (
                        <div className={theme.menu} onClick={(e) => e.stopPropagation()}>
                            <DropdownContextMenu>
                                <Dropdown.Item
                                    icon={faEdit}
                                    onClick={() => setShowEditDialog(true)}
                                    disabled={!optionGroup.permissions.canEdit}>
                                    <Trans>Edit</Trans>
                                </Dropdown.Item>
                                <Dropdown.Item icon={faCopy} as={ProjectRelativeLink} to={`/options/${optionGroup.id}/copy`}>
                                    <Trans>Duplicate</Trans>
                                </Dropdown.Item>
                                <Dropdown.Item icon={faHistory} onClick={() => setShowHistoryLogModal(true)}>
                                    <Trans>View log</Trans>
                                </Dropdown.Item>
                                <Dropdown.Item icon={faTrash} onClick={() => setShowDeleteDialog(true)}>
                                    <Trans>Delete</Trans>
                                </Dropdown.Item>
                            </DropdownContextMenu>
                        </div>
                    )}
                </div>
                <OptionGroupEditDialog
                    open={showEditDialog}
                    toggle={setShowEditDialog}
                    optionGroup={optionGroup}
                    onAccept={handleChange}
                />
                <OptionGroupDeleteDialog
                    optionGroup={optionGroup}
                    open={showDeleteDialog}
                    onOpenChange={setShowDeleteDialog}
                    onDeleted={handleChange}
                />
                <HistoryLogModal
                    hiddenColumns={['objectName']}
                    toggle={() => setShowHistoryLogModal(false)}
                    isOpen={showHistoryLogModal}
                    filter={{
                        projectId: optionGroup.projectId,
                        objectType: 'ProjectCatalogLinkGroup',
                        objectIds: [optionGroup.id],
                    }}
                />
            </div>
        );
    };

    return (
        <Accordion variant={accordionVariant} title={renderTitle()} lazy isOpen={open} onToggle={setOpen}>
            <OptionGroupAccordionDetails optionGroup={optionGroup} readonly={readonly} />
        </Accordion>
    );
};
